import React from 'react';

import { FlexProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessagingBasicsSection } from 'components/SmartAssistantSections/MessagingSmartAssistant/MessagingBasicsSection';
import { useMessagingSmartAssistantForms } from 'hooks/SmartAssistantForms/use-messaging-smart-assistant-forms';
import { useMultiUpdateNotificationHandling } from 'hooks/use-multi-update-notification-handling';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { DiscordService } from 'services/discord-service';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import {
  DiscordIntegration,
  DiscordInviteLinkValidationDto,
  FacebookIntegration,
  InstagramIntegration,
  SmartAssistant,
  SmartAssistantType,
  WhatsAppIntegration
} from 'shared/interfaces/SmartAssistant';

interface MessagingBasicsManagementSectionProps extends FlexProps {
  smartAssistant: SmartAssistant;
}

export function MessagingBasicsManagementSection({ smartAssistant }: MessagingBasicsManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { basicsForm, discordForm, facebookForm, instagramForm, whatsAppForm } =
    useMessagingSmartAssistantForms(smartAssistant);
  const { showUpdateNotification, resetNotificationStatusTracking } = useMultiUpdateNotificationHandling({
    feedbackKey: 'UPDATE_MESSAGING_BASICS'
  });

  const updateSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      basicsForm.resetDirty();
      showUpdateNotification(true);
    },
    onError: () => showUpdateNotification(false)
  });

  const updateIntegrationsMutation = useMutation({
    mutationFn: IntegrationManagerService.updateSmartAssistantIntegrations,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData(
        [SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
        () => updatedSmartAssistant
      );
      discordForm.resetDirty();
      facebookForm.resetDirty();
      instagramForm.resetDirty();
      whatsAppForm.resetDirty();
      showUpdateNotification(true);
    },
    onError: () => showUpdateNotification(false)
  });

  const validateDiscordInviteLinkMutation = useMutation({
    mutationFn: DiscordService.validateInviteLink,
    onSuccess: (discordInviteLinkValidation: DiscordInviteLinkValidationDto) => {
      if (!discordInviteLinkValidation.isValid) {
        discordForm.setFieldError('discordInviteLink', t('FORM_VALIDATION.UNAUTHORIZED_DISCORD_SERVER'));
      }
    },
    onError: () => {
      discordForm.setFieldError('discordInviteLink', t('FORM_VALIDATION.UNAUTHORIZED_DISCORD_SERVER'));
    }
  });

  const onSave = async () => {
    resetNotificationStatusTracking();
    let discordIntegration: DiscordIntegration | undefined;
    let facebookIntegration: FacebookIntegration | undefined;
    let instagramIntegration: InstagramIntegration | undefined;
    let whatsAppIntegration: WhatsAppIntegration | undefined;

    const basicsFormValidation = basicsForm.validate();
    if (basicsFormValidation.hasErrors) return;

    updateSmartAssistantMutation.mutate({
      id: smartAssistant._id,
      name: basicsForm.values.name
    });

    if (smartAssistant.type === SmartAssistantType.FACEBOOK) {
      const facebookFormValidation = facebookForm.validate();
      if (facebookFormValidation.hasErrors) return;
      facebookIntegration = {
        facebookAccount: facebookForm.values.facebookAccount
      };
    }

    if (smartAssistant.type === SmartAssistantType.WHATSAPP) {
      const whatsAppFormValidation = whatsAppForm.validate();
      if (whatsAppFormValidation.hasErrors) return;
      whatsAppIntegration = {
        whatsAppAccount: whatsAppForm.values.whatsAppAccount,
        whatsAppPhoneNumberId: whatsAppForm.values.whatsAppPhoneNumberId
      };
    }

    if (smartAssistant.type === SmartAssistantType.INSTAGRAM) {
      const instagramFormValidation = instagramForm.validate();
      if (instagramFormValidation.hasErrors) return;
      instagramIntegration = {
        instagramAccount: instagramForm.values.instagramAccount
      };
    }

    if (smartAssistant.type === SmartAssistantType.DISCORD) {
      const discordFormValidation = discordForm.validate();
      if (discordFormValidation.hasErrors) return;

      const discordIntegrationValidation = await validateDiscordInviteLinkMutation.mutateAsync(
        discordForm.values.discordInviteLink
      );
      if (!discordIntegrationValidation || !discordIntegrationValidation.isValid) return;

      discordIntegration = {
        serverId: discordIntegrationValidation.serverId,
        inviteLink: discordForm.values.discordInviteLink,
        invocationCommand: discordForm.values.discordInvocationCommand
      };
    }

    const shouldUpdateIntegrations =
      discordIntegration || facebookIntegration || instagramIntegration || whatsAppIntegration;
    if (shouldUpdateIntegrations) {
      updateIntegrationsMutation.mutate({
        smartAssistantId: smartAssistant._id,
        discordIntegration,
        facebookIntegration,
        instagramIntegration,
        whatsAppIntegration
      });
    }
  };

  const onCancel = () => {
    resetNotificationStatusTracking();
    resetForms();
  };

  const resetForms = () => {
    basicsForm.reset();
    discordForm.reset();
    facebookForm.reset();
    instagramForm.reset();
    whatsAppForm.reset();
  };

  // Add more mutaiton.isLoading if needed
  const integrationValidationLoading = validateDiscordInviteLinkMutation.isLoading;

  const hasChanges =
    basicsForm.isDirty() ||
    discordForm.isDirty() ||
    facebookForm.isDirty() ||
    instagramForm.isDirty() ||
    whatsAppForm.isDirty();

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.MESSAGING_BASICS_SECTION.TITLE')}
      hasChanges={hasChanges}
      loading={updateIntegrationsMutation.isLoading || integrationValidationLoading}
      onSave={onSave}
      onCancel={onCancel}>
      <MessagingBasicsSection
        basicsForm={basicsForm}
        smartAssistant={smartAssistant}
        integrationForm={{
          discordForm: smartAssistant.type === SmartAssistantType.DISCORD ? discordForm : undefined,
          facebookForm: smartAssistant.type === SmartAssistantType.FACEBOOK ? facebookForm : undefined,
          instagramForm: smartAssistant.type === SmartAssistantType.INSTAGRAM ? instagramForm : undefined,
          whatsAppForm: smartAssistant.type === SmartAssistantType.WHATSAPP ? whatsAppForm : undefined
        }}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
